/**
 * BasinAttributionLayer -- upstream supplier mines + gas-basin pins.
 *
 * Symbology:
 *   - Coal supplier  → rotated-square diamond (surface mine, open-pit feel)
 *   - Gas / oil well → triangle (wellhead / derrick feel)
 *   - Other fuel     → circle (no geometric association)
 *
 *   - Filled pin = data-confirmed source (EIA-923 MSHA match or
 *     EIA-923 gas-state match for this specific plant)
 *   - Outlined pin = national / fallback attribution (plant had no
 *     per-plant EIA-923 receipts; we used the national-average mix)
 *
 * Each pin also gets a faint disturbance-footprint halo (15 km) that
 * hints at the physical scale of the impact at that location — unlike
 * a stack plume, the mining/drilling impact is CENTRED on the pin
 * rather than drifting downwind.
 *
 * Supply arcs (plant → basin pin) still render only for the currently
 * hovered plant to avoid 100+ overlapping lines on dense fleets.
 */

import { useMemo } from "react";
import { Circle, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import type { DisplacedGeneratorEnriched } from "../../model/domain/types";
import {
  fmtMetric,
  getFuelDisplayName,
  isNamedSource,
  upstreamSourceLabel,
} from "../helpers";

// ──────────────────────────────────────────────────────────────────────
// Symbol -> fuel mapping
// ──────────────────────────────────────────────────────────────────────

type SupplierShape = "diamond" | "triangle" | "circle";

function shapeForFuel(fuel: string | undefined): SupplierShape {
  if (!fuel) return "circle";
  const f = fuel.toLowerCase();
  if (f.includes("coal") || f === "col") return "diamond";
  if (f.includes("gas") || f === "ng" || f === "natural_gas") return "triangle";
  if (f.includes("oil") || f === "petroleum") return "triangle";
  return "circle";
}

// Legend-friendly name for the geometry
const SHAPE_LABEL: Record<SupplierShape, string> = {
  diamond: "coal mine",
  triangle: "gas / oil supply region",
  circle: "other fuel supplier",
};

// ──────────────────────────────────────────────────────────────────────
// SVG divIcon — keeps the shapes in-component (no external asset)
// ──────────────────────────────────────────────────────────────────────

function pinIcon(shape: SupplierShape, filled: boolean): L.DivIcon {
  const size = 14;
  const fill = filled ? "#0F172A" : "rgba(15,23,42,0.08)";
  const stroke = filled ? "#F3F4F6" : "#64748B";
  const strokeWidth = filled ? 1.5 : 1.25;
  let body: string;
  if (shape === "diamond") {
    body = `<path d="M7 1 L13 7 L7 13 L1 7 Z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="miter"/>`;
  } else if (shape === "triangle") {
    body = `<path d="M7 1.5 L12.75 12 L1.25 12 Z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="miter"/>`;
  } else {
    body = `<circle cx="7" cy="7" r="5.2" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
  }
  return L.divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 14 14">${body}</svg>`,
    className: "basin-pin-svg",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// ──────────────────────────────────────────────────────────────────────
// Pin data shape
// ──────────────────────────────────────────────────────────────────────

interface BasinPin {
  plantId: string | number;
  plantFuel: string;
  key: string;
  lat: number;
  lon: number;
  named: boolean;
  label: string;
  source: string;
  share: number;
  dalysShare: number;
  pdfsShare: number;
  usdShare: number;
  basin?: string | null;
  state?: string | null;
  county?: string | null;
  mineMshaId?: string | null;
}

function collectPins(plants: DisplacedGeneratorEnriched[]): BasinPin[] {
  const pins: BasinPin[] = [];
  for (const p of plants) {
    const entries = p.upstream_attribution ?? [];
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      if (!e.latitude || !e.longitude) continue;
      pins.push({
        plantId: p.plant_id,
        plantFuel: p.fuel_type,
        key: `${p.plant_id}-${i}-${e.basin_name}`,
        lat: e.latitude,
        lon: e.longitude,
        named: isNamedSource(e.source),
        label: e.basin_name,
        source: e.source ?? "national_average",
        share: e.share,
        dalysShare: e.dalys_share,
        pdfsShare: e.pdfs_share,
        usdShare: e.usd_share,
        basin: e.basin,
        state: e.state,
        county: e.county,
        mineMshaId: e.mine_msha_id,
      });
    }
  }
  return pins;
}

// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────

interface Props {
  plants: DisplacedGeneratorEnriched[];
  hoveredPlantId: string | number | null;
}

// Disturbance halo radius (meters). Hints at the "physical extent of
// mining/drilling impact" without pretending it's a precise footprint.
// Coal mines: ~15 km covers a typical surface-mine envelope (~5-10 km
// active pit + bench + haul roads + overburden). Gas basins are much
// more diffuse (wells spread over 100+ km) but we keep the halo small
// so it doesn't dominate the map.
const HALO_RADIUS_M_COAL = 15_000;
const HALO_RADIUS_M_GAS = 25_000;
const HALO_RADIUS_M_OTHER = 12_000;

function haloRadius(shape: SupplierShape): number {
  if (shape === "diamond") return HALO_RADIUS_M_COAL;
  if (shape === "triangle") return HALO_RADIUS_M_GAS;
  return HALO_RADIUS_M_OTHER;
}

export function BasinAttributionLayer({ plants, hoveredPlantId }: Props) {
  const pins = useMemo(() => collectPins(plants), [plants]);

  const plantsById = useMemo(() => {
    const m = new Map<string, DisplacedGeneratorEnriched>();
    for (const p of plants) m.set(String(p.plant_id), p);
    return m;
  }, [plants]);

  return (
    <>
      {/* Supply arcs — only for the hovered plant */}
      {hoveredPlantId != null &&
        pins
          .filter((pin) => String(pin.plantId) === String(hoveredPlantId))
          .map((pin) => {
            const plant = plantsById.get(String(pin.plantId));
            if (!plant?.latitude || !plant?.longitude) return null;
            return (
              <Polyline
                key={`arc-${pin.key}`}
                positions={[
                  [plant.latitude, plant.longitude],
                  [pin.lat, pin.lon],
                ]}
                pathOptions={{
                  color: "#9CA3AF",
                  weight: 1,
                  opacity: 0.55,
                  dashArray: "3 5",
                }}
                interactive={false}
              />
            );
          })}

      {/* Disturbance halos — behind the pins so the pin sits on top */}
      {pins.map((pin) => {
        const shape = shapeForFuel(pin.plantFuel);
        return (
          <Circle
            key={`halo-${pin.key}`}
            center={[pin.lat, pin.lon]}
            radius={haloRadius(shape)}
            pathOptions={{
              fillColor: "#0F172A",
              color: "#0F172A",
              weight: 0.5,
              fillOpacity: pin.named ? 0.08 : 0.04,
              opacity: pin.named ? 0.35 : 0.18,
            }}
            interactive={false}
          />
        );
      })}

      {/* Pins — shape by fuel, fill by data confidence */}
      {pins.map((pin) => {
        const shape = shapeForFuel(pin.plantFuel);
        return (
          <Marker
            key={`pin-${pin.key}`}
            position={[pin.lat, pin.lon]}
            icon={pinIcon(shape, pin.named)}
          >
            <Popup className="spatial-popup">
              <div className="text-sm min-w-[230px] text-gray-800">
                <p className="font-semibold text-gray-900 text-[15px] mb-1">
                  {pin.label}
                </p>
                <p className="text-gray-600 text-xs mb-0.5">
                  {SHAPE_LABEL[shape]} · {getFuelDisplayName(pin.plantFuel)}
                </p>
                <p className="text-gray-600 text-xs mb-0.5">
                  {upstreamSourceLabel(pin.source as any)}
                </p>
                {pin.basin && (
                  <p className="text-gray-600 text-xs">
                    <span className="text-gray-500">Basin:</span> {pin.basin}
                  </p>
                )}
                {(pin.county || pin.state) && (
                  <p className="text-gray-600 text-xs">
                    {[pin.county, pin.state].filter(Boolean).join(", ")}
                  </p>
                )}
                {pin.mineMshaId && (
                  <p className="text-gray-500 text-[11px]">MSHA ID: {pin.mineMshaId}</p>
                )}
                <div className="mt-2 pt-2 border-t border-gray-200 space-y-0.5">
                  <div className="flex justify-between gap-3 text-gray-700">
                    <span>Supply share</span>
                    <span className="text-gray-900 tabular-nums font-medium">
                      {(pin.share * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between gap-3 text-gray-700">
                    <span>DALYs attributed</span>
                    <span className="text-gray-900 tabular-nums font-medium">
                      {fmtMetric(pin.dalysShare, "DALYs")}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3 text-gray-700">
                    <span>Species·yr attributed</span>
                    <span className="text-gray-900 tabular-nums font-medium">
                      {fmtMetric(pin.pdfsShare, "PDFs")}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3 text-gray-700">
                    <span>USD attributed</span>
                    <span className="text-gray-900 tabular-nums font-medium">
                      {fmtMetric(pin.usdShare, "USD")}
                    </span>
                  </div>
                </div>
                <p className="mt-2 pt-2 border-t border-gray-200 text-[10px] text-gray-500 leading-tight">
                  {pin.named
                    ? "Confidence: high — matched to this plant via EIA-923 fuel receipts."
                    : "Confidence: low — plant has no EIA-923 receipts on file; using US national-average supply mix."}
                </p>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Legend chip
// ──────────────────────────────────────────────────────────────────────

function svgChip(shape: SupplierShape, filled: boolean) {
  const fill = filled ? "#0F172A" : "transparent";
  const stroke = filled ? "#0F172A" : "#64748B";
  const size = 11;
  if (shape === "diamond") {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" className="inline-block">
        <path d="M7 1 L13 7 L7 13 L1 7 Z" fill={fill} stroke={stroke} strokeWidth={1.2} />
      </svg>
    );
  }
  if (shape === "triangle") {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" className="inline-block">
        <path d="M7 1.5 L12.75 12 L1.25 12 Z" fill={fill} stroke={stroke} strokeWidth={1.2} />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" className="inline-block">
      <circle cx="7" cy="7" r="5.2" fill={fill} stroke={stroke} strokeWidth={1.2} />
    </svg>
  );
}

export function BasinLegendChip() {
  return (
    <span className="inline-flex items-center gap-2 text-[10px] text-text-muted flex-wrap">
      {svgChip("diamond", true)}
      <span>coal mine</span>
      {svgChip("triangle", true)}
      <span>gas / oil</span>
      {svgChip("diamond", false)}
      <span className="italic">= national fallback (lower confidence)</span>
    </span>
  );
}
